import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { useAuthStore } from '../store/useStore';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Code2, Copy, ExternalLink, Palette, Eye, Settings, Check,
  RefreshCw, Loader2, Monitor, Smartphone, Tablet, Calendar,
  Layout, Type, Link2, QrCode, Zap, Globe
} from 'lucide-react';
import WidgetIntegrationPanel from '../components/widgets/WidgetIntegrationPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LAYOUT_OPTIONS = [
  { value: 'grid',     label: 'Cuadrícula', icon: Layout },
  { value: 'list',     label: 'Lista',       icon: Type },
  { value: 'carousel', label: 'Carrusel',    icon: Monitor },
];

const FONT_OPTIONS = [
  { value: 'inter',    label: 'Inter (moderno)' },
  { value: 'poppins',  label: 'Poppins (amigable)' },
  { value: 'playfair', label: 'Playfair (elegante)' },
];

const RADIUS_OPTIONS = [
  { value: '4',  label: 'Cuadrado' },
  { value: '8',  label: 'Suave' },
  { value: '16', label: 'Redondeado' },
  { value: '24', label: 'Muy redondeado' },
];

const DEFAULT_CONFIG = {
  primaryColor: '#F97316',
  backgroundColor: '#FFFFFF',
  textColor: '#1E293B',
  layout: 'grid',
  fontFamily: 'inter',
  borderRadius: '12',
  showPrice: true,
  showDate: true,
  showCapacity: true,
  showDescription: false,
  showSearch: true,
  maxEvents: 6,
  buttonText: 'Comprar tickets',
  buttonStyle: 'filled',
  theme: 'light',
};

function EventWidgetPreview({ events, config, selectedEvent }) {
  const items = events.filter(e => !selectedEvent || e.id === selectedEvent).slice(0, config.maxEvents);
  const isDark = config.theme === 'dark';
  const br = `${config.borderRadius}px`;
  const fontFamily = { inter: "'Inter',sans-serif", poppins: "'Poppins',sans-serif", playfair: "'Playfair Display',serif" }[config.fontFamily] || "'Inter',sans-serif";

  const wrap = { backgroundColor: config.backgroundColor, color: config.textColor, fontFamily, padding: '28px', borderRadius: br, minHeight: 320, boxSizing: 'border-box' };
  const card = { backgroundColor: isDark ? '#1e293b' : '#ffffff', borderRadius: br, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
  const tag = (color) => ({ fontSize: '11px', padding: '2px 10px', borderRadius: '100px', backgroundColor: color, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '3px' });

  const btn = {
    backgroundColor: config.buttonStyle === 'filled' ? config.primaryColor : config.buttonStyle === 'outline' ? 'transparent' : 'transparent',
    color: config.buttonStyle === 'filled' ? '#fff' : config.primaryColor,
    border: config.buttonStyle === 'ghost' ? 'none' : `2px solid ${config.primaryColor}`,
    borderRadius: `calc(${config.borderRadius}px - 2px)`,
    padding: '9px 18px', fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', width: '100%', marginTop: '10px', fontFamily,
  };

  return (
    <div style={wrap}>
      {config.showSearch && (
        <div style={{ marginBottom: '20px' }}>
          <input readOnly placeholder="🔍  Buscar eventos..." style={{ width: '100%', padding: '10px 14px', borderRadius: br, border: `1.5px solid ${isDark ? '#475569' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#f8fafc', color: config.textColor, fontFamily, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }} />
        </div>
      )}
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', opacity: 0.4 }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📅</div>
          <p style={{ fontSize: '14px', margin: 0 }}>No hay eventos disponibles</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: config.layout === 'list' ? '1fr' : 'repeat(auto-fill,minmax(200px,1fr))', gap: '16px' }}>
          {items.map(ev => (
            <div key={ev.id} style={{ ...card, display: config.layout === 'list' ? 'flex' : 'block', gap: '0' }}>
              <div style={{ background: `linear-gradient(135deg,${config.primaryColor}25,${config.primaryColor}50)`, height: config.layout === 'list' ? 'auto' : '130px', width: config.layout === 'list' ? '110px' : '100%', minHeight: config.layout === 'list' ? '80px' : 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>
                🎉
              </div>
              <div style={{ padding: '14px', flex: 1 }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 700, color: config.textColor, lineHeight: 1.3 }}>{ev.name}</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '10px' }}>
                  {config.showDate && ev.date && <span style={{ ...tag(`${config.primaryColor}18`), color: config.primaryColor }}>{new Date(ev.date + 'T12:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>}
                  {config.showCapacity && ev.capacity && <span style={{ ...tag(isDark ? '#334155' : '#f1f5f9'), color: config.textColor, opacity: 0.75 }}>👥 {ev.capacity}</span>}
                  {config.showPrice && ev.price != null && <span style={{ ...tag('#dcfce7'), color: '#16a34a' }}>{ev.price === 0 ? 'Gratis' : `€${ev.price}`}</span>}
                </div>
                <button style={btn}>{config.buttonText}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EventWidgetBuilder() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [copied, setCopied] = useState(false);
  const [viewport, setViewport] = useState('desktop');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [evRes, restRes] = await Promise.all([
        api.get('/events'),
        api.get('/restaurant'),
      ]);
      const evList = Array.isArray(evRes.data) ? evRes.data : [];
      setEvents(evList);
      setRestaurant(restRes.data);
      if (evList.length > 0) setSelectedEvent(evList[0].id);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setC = (key, value) => setConfig(c => ({ ...c, [key]: value }));

  const widgetUrl = (() => {
    if (!restaurant?.slug && !user?.restaurant_id) return '';
    const base = 'https://okreserva.okgo.mx';
    const id = restaurant?.slug || user?.restaurant_id;
    const params = new URLSearchParams({
      layout: config.layout,
      color: config.primaryColor.replace('#', ''),
      bg: config.backgroundColor.replace('#', ''),
      text: config.textColor.replace('#', ''),
      radius: config.borderRadius,
      font: config.fontFamily,
      maxEvents: config.maxEvents,
      showPrice: config.showPrice,
      showDate: config.showDate,
      showCapacity: config.showCapacity,
      showSearch: config.showSearch,
      btnText: encodeURIComponent(config.buttonText),
      btnStyle: config.buttonStyle,
      theme: config.theme,
    });
    if (selectedEvent) params.set('event', selectedEvent);
    return `${base}/widget/events/${id}?${params}`;
  })();

  const iframeCode = `<iframe
  src="${widgetUrl}"
  width="100%"
  height="${config.layout === 'list' ? '600' : '700'}"
  frameborder="0"
  style="border-radius: ${config.borderRadius}px; max-width: 900px; display: block; margin: 0 auto;"
  loading="lazy"
  title="Reserva de Eventos"
></iframe>`;

  const scriptCode = `<div id="ok-events-widget" data-restaurant="${restaurant?.slug || user?.restaurant_id}" data-config='${JSON.stringify(config)}'></div>
<script src="${API_URL?.replace('/api', '')}/widget.js" async defer></script>`;

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Código copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const viewportW = { desktop: '100%', tablet: '768px', mobile: '390px' };

  if (loading) return (
    <div className="min-h-screen flex flex-col">
      <TopBar title="Widget de Eventos" />
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar
        title="Widget de Eventos"
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-8 text-xs bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { localStorage.setItem(`widget_events_config_${user?.restaurant_id}`, JSON.stringify(config)); toast.success('Configuración guardada'); }}>
              <Globe className="h-3.5 w-3.5 mr-1" /> Guardar
            </Button>
            <a href={widgetUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="h-8 text-xs">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver en vivo
              </Button>
            </a>
          </div>
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* ── Config Panel ── */}
        <div className="w-72 bg-white border-r flex flex-col overflow-y-auto shadow-sm flex-shrink-0">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
              <Settings className="h-4 w-4 text-orange-500" /> Configuración
            </h2>
          </div>

          <div className="p-4 space-y-5 flex-1">
            {/* Event selector */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Evento destacado</Label>
              <Select value={selectedEvent || 'all'} onValueChange={v => setSelectedEvent(v === 'all' ? null : v)}>
                <SelectTrigger className="text-xs h-8"><SelectValue placeholder="Todos los eventos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los eventos</SelectItem>
                  {events.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Layout */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Distribución</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {LAYOUT_OPTIONS.map(l => (
                  <button key={l.value} onClick={() => setC('layout', l.value)}
                    className={cn("py-2 rounded-lg border text-[11px] font-medium transition-all flex flex-col items-center gap-1",
                      config.layout === l.value
                        ? "border-orange-500 bg-orange-50 text-orange-700"
                        : "border-slate-200 text-slate-500 hover:border-slate-300")}>
                    <l.icon className="h-4 w-4" />
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Tema</Label>
              <div className="flex gap-2">
                {['light', 'dark'].map(t => (
                  <button key={t} onClick={() => setC('theme', t)}
                    className={cn("flex-1 py-1.5 rounded-lg border text-xs font-medium capitalize transition-all",
                      config.theme === t ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-500 hover:border-slate-300")}>
                    {t === 'light' ? '☀️ Claro' : '🌙 Oscuro'}
                  </button>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Colores</Label>
              <div className="space-y-2">
                {[
                  { key: 'primaryColor', label: 'Color principal' },
                  { key: 'backgroundColor', label: 'Fondo' },
                  { key: 'textColor', label: 'Texto' },
                ].map(c => (
                  <div key={c.key} className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">{c.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 font-mono">{config[c.key]}</span>
                      <input type="color" value={config[c.key]}
                        onChange={e => setC(c.key, e.target.value)}
                        className="h-7 w-9 rounded border border-slate-200 cursor-pointer p-0.5" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Typography & Shape */}
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block">Tipografía</Label>
                <Select value={config.fontFamily} onValueChange={v => setC('fontFamily', v)}>
                  <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block">Bordes</Label>
                <Select value={config.borderRadius} onValueChange={v => setC('borderRadius', v)}>
                  <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RADIUS_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Visibility toggles */}
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Mostrar elementos</Label>
              <div className="space-y-2">
                {[
                  { key: 'showPrice',       label: 'Precio' },
                  { key: 'showDate',        label: 'Fecha y hora' },
                  { key: 'showCapacity',    label: 'Disponibilidad' },
                  { key: 'showDescription', label: 'Descripción' },
                  { key: 'showSearch',      label: 'Buscador' },
                ].map(t => (
                  <label key={t.key} className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-slate-600">{t.label}</span>
                    <Switch checked={config[t.key]} onCheckedChange={v => setC(t.key, v)} />
                  </label>
                ))}
              </div>
            </div>

            {/* Button */}
            <div className="space-y-2">
              <Label className="text-xs text-slate-500 block">Botón de acción</Label>
              <Input value={config.buttonText}
                onChange={e => setC('buttonText', e.target.value)}
                placeholder="Comprar tickets" className="text-xs h-8" />
              <div className="flex gap-2">
                {['filled', 'outline', 'ghost'].map(s => (
                  <button key={s} onClick={() => setC('buttonStyle', s)}
                    className={cn("flex-1 py-1.5 rounded-lg border text-[11px] font-medium capitalize transition-all",
                      config.buttonStyle === s ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-500")}>
                    {s === 'filled' ? 'Relleno' : s === 'outline' ? 'Borde' : 'Texto'}
                  </button>
                ))}
              </div>
            </div>

            {/* Max events */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Eventos a mostrar: {config.maxEvents}</Label>
              <input type="range" min={1} max={20} value={config.maxEvents}
                onChange={e => setC('maxEvents', +e.target.value)}
                className="w-full accent-orange-500" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                <span>1</span><span>20</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Preview + Code Panel ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Tabs defaultValue="preview" className="flex flex-col flex-1 overflow-hidden">
            {/* Tabs header */}
            <div className="bg-white border-b px-4 flex items-center gap-2">
              <TabsList className="h-9 bg-transparent">
                <TabsTrigger value="preview" className="text-xs data-[state=active]:bg-slate-100 rounded-lg">
                  <Eye className="h-3.5 w-3.5 mr-1.5" /> Vista previa
                </TabsTrigger>
                <TabsTrigger value="code" className="text-xs data-[state=active]:bg-slate-100 rounded-lg">
                  <Code2 className="h-3.5 w-3.5 mr-1.5" /> Integración
                </TabsTrigger>
              </TabsList>

              {/* Viewport selector */}
              <div className="ml-auto flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
                {[
                  { k: 'desktop', icon: Monitor },
                  { k: 'tablet',  icon: Tablet },
                  { k: 'mobile',  icon: Smartphone },
                ].map(v => (
                  <button key={v.k} onClick={() => setViewport(v.k)}
                    className={cn("p-1.5 rounded-md transition-all",
                      viewport === v.k ? "bg-white shadow-sm text-slate-800" : "text-slate-400 hover:text-slate-600")}>
                    <v.icon className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <TabsContent value="preview" className="flex-1 overflow-auto p-6 m-0 flex justify-center bg-slate-100">
              <div className="transition-all duration-300 w-full" style={{ maxWidth: viewportW[viewport] === '100%' ? '100%' : viewportW[viewport] }}>
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 bg-white rounded-full px-3 py-1 text-[10px] text-slate-400 font-mono">
                    yourwebsite.com
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200">
                  <EventWidgetPreview events={events} config={config} selectedEvent={selectedEvent} />
                </div>
              </div>
            </TabsContent>

            {/* Integration panel */}
            <TabsContent value="code" className="flex-1 overflow-auto p-6 m-0 bg-slate-50">
              <div className="max-w-6xl mx-auto">
                <WidgetIntegrationPanel
                  type="events"
                  restaurantId={user?.restaurant_id || 'YOUR_RESTAURANT_ID'}
                  urlParams={{
                    layout: config.layout,
                    color: config.primaryColor.replace('#', ''),
                    bg: config.backgroundColor.replace('#', ''),
                    text: config.textColor.replace('#', ''),
                    radius: config.borderRadius,
                    font: config.fontFamily,
                    maxEvents: config.maxEvents,
                    showPrice: config.showPrice,
                    showDate: config.showDate,
                    showCapacity: config.showCapacity,
                    showSearch: config.showSearch,
                    btnStyle: config.buttonStyle,
                    theme: config.theme,
                    ...(selectedEvent ? { event: selectedEvent } : {}),
                  }}
                  sdkConfig={config}
                  borderRadius={`${config.borderRadius}px`}
                  embedTitle="Widget de Eventos"
                />
              </div>
            </TabsContent>

            {/* DEPRECATED legacy tabs removed (iframe/script/qr) */}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
